import {useCallback, useEffect, useSyncExternalStore} from "react";
import {useDispatch, useSelector} from "react-redux";
import {setOnDeviceUnconfirmed} from "../../../axios-config/axiosPrivate";
import {initiateLogout} from "../../../store/auth/authActions";
import {selectIsDeviceConfirmed, selectIsLoggedIn, selectIsReadLocked} from "../../../store/auth/authSelector";
import DeviceUnlockDialog from "./DeviceUnlockDialog";
import {
    isDeviceUnlockRequested,
    requestDeviceUnlock,
    settleDeviceUnlock,
    subscribeDeviceUnlock,
} from "./deviceUnlockController";

/**
 * Shows the "unlock device" dialog, once, in the two cases that need it:
 * - a request was answered with 403 `DEVICE_UNCONFIRMED` (the private Axios instance asks via
 *   `setOnDeviceUnconfirmed`): the user may cancel, which hands the 403 back to the caller;
 * - the access token says `readLocked` (a guest on an unknown device): the dialog stays until the code is
 *   confirmed or the user logs out. The app loads its data only after that (see App.jsx).
 *
 * Mount it once, inside the Redux provider, query client and theme.
 */
const DeviceUnlockHost = () => {
    const dispatch = useDispatch();
    const isLoggedIn = useSelector(selectIsLoggedIn);
    const isReadLocked = useSelector(selectIsReadLocked);
    const isDeviceConfirmed = useSelector(selectIsDeviceConfirmed);
    const isRequested = useSyncExternalStore(subscribeDeviceUnlock, isDeviceUnlockRequested);

    useEffect(() => {
        setOnDeviceUnconfirmed(requestDeviceUnlock);
        return () => {
            setOnDeviceUnconfirmed();
            settleDeviceUnlock(false);
        };
    }, []);

    // The waiting requests are over with the session, and they can go on when another tab confirmed the device.
    useEffect(() => {
        if (!isLoggedIn) {
            settleDeviceUnlock(false);
        } else if (isDeviceConfirmed) {
            settleDeviceUnlock(true);
        }
    }, [isLoggedIn, isDeviceConfirmed]);

    const handleCancel = useCallback(() => settleDeviceUnlock(false), []);
    const handleConfirmed = useCallback(() => settleDeviceUnlock(true), []);
    const handleLogout = useCallback(() => {
        settleDeviceUnlock(false);
        dispatch(initiateLogout());
    }, [dispatch]);

    if (!isLoggedIn || !(isReadLocked || isRequested)) {
        return null;
    }
    return (
        <DeviceUnlockDialog
            onCancel={isReadLocked ? undefined : handleCancel}
            onLogout={handleLogout}
            onConfirmed={handleConfirmed}
        />
    );
};

export default DeviceUnlockHost;
